package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.domain.model.NegativeSatisfactionVote
import br.com.vvrefeicoes.portal.domain.model.OperationStatus
import br.com.vvrefeicoes.portal.domain.model.SatisfactionCompany
import br.com.vvrefeicoes.portal.domain.model.SatisfactionCompanyService
import br.com.vvrefeicoes.portal.domain.model.SatisfactionDailyVotes
import br.com.vvrefeicoes.portal.domain.model.SatisfactionMetric
import br.com.vvrefeicoes.portal.domain.model.SatisfactionRankingItem
import br.com.vvrefeicoes.portal.domain.model.SatisfactionReport
import br.com.vvrefeicoes.portal.domain.model.SatisfactionServiceVotes
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import br.com.vvrefeicoes.portal.domain.repository.SatisfactionReportsRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.http.encodeURLParameter
import kotlinx.serialization.Serializable

class RemoteSatisfactionReportsRepository(
    private val httpClient: HttpClient,
    private val authRepository: AuthRepository,
    private val baseUrl: String,
) : SatisfactionReportsRepository {
    override suspend fun loadCompanies(): List<SatisfactionCompany> = request<SatisfactionPageDto<SatisfactionCompanyDto>>("/satisfaction/reports/companies").content.map { it.toDomain() }

    override suspend fun loadReport(
        companyId: String,
        startDate: String,
        endDate: String,
    ): SatisfactionReport = loadReports(listOf(companyId), startDate, endDate)[companyId] ?: emptyReport()

    override suspend fun loadReports(
        companyIds: List<String>,
        startDate: String,
        endDate: String,
    ): Map<String, SatisfactionReport> {
        if (companyIds.isEmpty()) return emptyMap()
        val response: HttpResponse =
            httpClient.post(baseUrl + "/satisfaction/analytics") {
                authRepository.session.value
                    ?.token
                    ?.let { header(HttpHeaders.Authorization, "Bearer $it") }
                contentType(ContentType.Application.Json)
                setBody(SatisfactionAnalyticsRequestDto(startDate, endDate, companyIds))
            }
        if (response.status.value !in 200..299) error("Satisfaction: HTTP ${response.status.value}")
        return response.body<SatisfactionAnalyticsResponseDto>().companies.mapValues { it.value.toDomain() }
    }

    override suspend fun loadOperationStatus(
        startDate: String,
        endDate: String,
        directorId: String?,
    ): OperationStatus {
        val query = query(startDate, endDate, directorId)
        return request<SatisfactionOperationStatusDto>("/satisfaction/reports/operation-status?$query").toDomain()
    }

    override suspend fun loadRanking(
        startDate: String,
        endDate: String,
        directorId: String?,
    ): List<SatisfactionRankingItem> {
        val query = query(startDate, endDate, directorId)
        return request<SatisfactionRankingDto>("/satisfaction/reports/company-ranking-complete?$query").ranking.map { it.toDomain() }
    }

    private suspend inline fun <reified T> request(path: String): T {
        val response: HttpResponse =
            httpClient.get(baseUrl + path) {
                authRepository.session.value
                    ?.token
                    ?.let { header(HttpHeaders.Authorization, "Bearer $it") }
            }
        if (response.status.value !in 200..299) error("Satisfaction: HTTP ${response.status.value}")
        return response.body()
    }

    private fun query(
        startDate: String,
        endDate: String,
        directorId: String?,
    ): String =
        buildString {
            append("startDate=").append(startDate.encodeURLParameter())
            append("&endDate=").append(endDate.encodeURLParameter())
            directorId?.let { append("&directorId=").append(it.encodeURLParameter()) }
        }
}

@Serializable
private data class SatisfactionCompanyDto(
    val id: String,
    val nome: String = "",
    val status: Boolean = true,
    val qtdbutao: Int = 0,
    val qt_funcionarios: Double? = null,
    val servicos: List<SatisfactionCompanyServiceDto> = emptyList(),
)

@Serializable
private data class SatisfactionCompanyServiceDto(
    val tipo_servico: String = "",
    val nome: String = "",
    val status: Boolean = true,
    val qtd_ref: Double? = null,
    val qtd_ref_diaria: Double? = null,
    val qtd_ref_dias_ativos: Map<String, List<Int>>? = null,
)

@Serializable
private data class SatisfactionAnalyticsRequestDto(
    val startDate: String,
    val endDate: String,
    val companyIds: List<String>,
)

@Serializable
private data class SatisfactionAnalyticsResponseDto(
    val companies: Map<String, SatisfactionAnalyticsDto> = emptyMap(),
)

@Serializable
private data class SatisfactionPageDto<T>(
    val content: List<T> = emptyList(),
)

@Serializable
private data class SatisfactionAnalyticsDto(
    val totalVotes: Int = 0,
    val avaliacoesPorTipo: Map<String, Int> = emptyMap(),
    val percentuaisPorTipo: Map<String, Double> = emptyMap(),
    val votesByDay: List<SatisfactionDailyDto> = emptyList(),
    val votesByService: Map<String, SatisfactionServiceVotesDto> = emptyMap(),
    val votosNegativos: List<SatisfactionNegativeDto> = emptyList(),
    val recentVotes: List<SatisfactionNegativeDto> = emptyList(),
)

@Serializable
private data class SatisfactionDailyDto(
    val data: String = "",
    val Ótimo: Int = 0,
    val Bom: Int = 0,
    val Regular: Int = 0,
    val Ruim: Int = 0,
    val total: Int = 0,
)

@Serializable
private data class SatisfactionServiceVotesDto(
    val total: Int = 0,
    val avaliacoes: Map<String, Int> = emptyMap(),
    val percentuais: Map<String, Double> = emptyMap(),
    val serviceInfo: SatisfactionServiceInfoDto? = null,
)

@Serializable
private data class SatisfactionServiceInfoDto(
    val nome: String = "",
    val tipo_servico: String = "",
    val hora_inicio: String? = null,
    val hora_final: String? = null,
    val qtd_ref: Double? = null,
    val qtd_ref_diaria: Double? = null,
    val qtd_ref_dias_ativos: Map<String, List<Int>>? = null,
)

@Serializable
private data class SatisfactionNegativeDto(
    val avaliacao: String = "",
    val comentario: String? = null,
    val momento_voto: String = "",
    val tipo_servico: SatisfactionServiceInfoDto? = null,
)

@Serializable
private data class SatisfactionOperationStatusDto(
    val metrics: SatisfactionMetricsDto = SatisfactionMetricsDto(),
)

@Serializable
private data class SatisfactionMetricsDto(
    val totalVotes: Int = 0,
    val satisfacao: SatisfactionMetricDto = SatisfactionMetricDto(),
    val melhoria: SatisfactionMetricDto = SatisfactionMetricDto(),
)

@Serializable
private data class SatisfactionMetricDto(
    val count: Int = 0,
    val percentage: Double = 0.0,
)

@Serializable
private data class SatisfactionRankingDto(
    val ranking: List<SatisfactionRankingItemDto> = emptyList(),
)

@Serializable
private data class SatisfactionRankingItemDto(
    val position: Int = 0,
    val companyId: String = "",
    val companyName: String = "",
    val qtRefeicoesDiarias: Double = 0.0,
    val totalVotes: Int = 0,
    val satisfacao: SatisfactionMetricDto = SatisfactionMetricDto(),
    val melhoria: SatisfactionMetricDto = SatisfactionMetricDto(),
)

private fun SatisfactionCompanyDto.toDomain() =
    SatisfactionCompany(
        id = id,
        name = nome,
        active = status,
        buttonCount = qtdbutao,
        employeeCount = qt_funcionarios ?: 0.0,
        services =
            servicos.map {
                SatisfactionCompanyService(
                    typeId = it.tipo_servico,
                    name = it.nome,
                    active = it.status,
                    dailyQuantity = it.qtd_ref_diaria ?: it.qtd_ref ?: 0.0,
                    activeDaysByMonth = it.qtd_ref_dias_ativos ?: emptyMap(),
                )
            },
    )

private fun SatisfactionAnalyticsDto.toDomain() =
    SatisfactionReport(
        totalVotes = totalVotes,
        ratings = avaliacoesPorTipo,
        percentages = percentuaisPorTipo,
        dailyVotes = votesByDay.map { SatisfactionDailyVotes(it.data, it.Ótimo, it.Bom, it.Regular, it.Ruim, it.total) },
        serviceVotes =
            votesByService.values.map { service ->
                val info = service.serviceInfo
                SatisfactionServiceVotes(
                    name = info?.nome.orEmpty(),
                    total = service.total,
                    ratings = service.avaliacoes,
                    percentages = service.percentuais,
                    typeId = info?.tipo_servico.orEmpty(),
                    startTime = info?.hora_inicio.orEmpty(),
                    endTime = info?.hora_final.orEmpty(),
                    dailyQuantity = info?.qtd_ref_diaria ?: info?.qtd_ref ?: 0.0,
                    activeDaysByMonth = info?.qtd_ref_dias_ativos ?: emptyMap(),
                )
            },
        negativeVotes = votosNegativos.map { NegativeSatisfactionVote(it.avaliacao, it.comentario.orEmpty(), it.momento_voto, it.tipo_servico?.nome.orEmpty()) },
        recentVotes = recentVotes.map { NegativeSatisfactionVote(it.avaliacao, it.comentario.orEmpty(), it.momento_voto, it.tipo_servico?.nome.orEmpty()) },
    )

private fun emptyReport() = SatisfactionReport(0, emptyMap(), emptyMap(), emptyList(), emptyList(), emptyList())

private fun SatisfactionOperationStatusDto.toDomain() =
    OperationStatus(
        metrics.totalVotes,
        SatisfactionMetric(metrics.satisfacao.count, metrics.satisfacao.percentage),
        SatisfactionMetric(metrics.melhoria.count, metrics.melhoria.percentage),
    )

private fun SatisfactionRankingItemDto.toDomain() =
    SatisfactionRankingItem(
        position,
        companyId,
        companyName,
        qtRefeicoesDiarias,
        totalVotes,
        SatisfactionMetric(satisfacao.count, satisfacao.percentage),
        SatisfactionMetric(melhoria.count, melhoria.percentage),
    )
